import { Text } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import {
  BallCollider,
  CuboidCollider,
  CylinderCollider,
  RigidBody,
} from "@react-three/rapier";
import { useRef, useMemo } from "react";
import * as THREE from "three";

export default function DynamicPlatforms() {
  const sideMovePlatformRef = useRef();
  const elevatingPlatformRefs = useRef([]);
  const rotatingPlatformRefs = useRef([]);
  const rotationDrumRef = useRef();
  const movingPlatformBase = useMemo(() => [21.81, 7, -9], []);
  const elevatingPlatforms = useMemo(
    () => [
      {
        basePosition: [-8.66, 23, 7.09],
        textRotation: [0, Math.PI / 2, 0],
        liftAmplitude: 2,
        heightOffset: 2,
      },
    ],
    []
  );
  const rotatingPlatforms = useMemo(
    () => [
      {
        basePosition: [6.5, 9, -23],
        shape: "box",
        rotationSpeed: 3,
        oscillationAmplitude: 4,
        oscillationPhase: 0,
      },
      {
        basePosition: [0, 10, -23],
        shape: "box",
        rotationSpeed: 3,
        oscillationAmplitude: 4,
        oscillationPhase: Math.PI,
      },
      {
        basePosition: [-6.5, 11, -23],
        shape: "box",
        rotationSpeed: 3,
        oscillationAmplitude: 4,
        oscillationPhase: 0,
      },
    ],
    []
  );

  // Initializ animation settings
  let time = null;
  const xRotationAxies = new THREE.Vector3(1, 0, 0);
  const yRotationAxies = new THREE.Vector3(0, 1, 0);
  const rotation = [Math.PI * .1, Math.PI * 0.55, 0]

  const quaternionRotation = useMemo(() => new THREE.Quaternion(), []);
  const rotationDrumBaseQuaternion = useMemo(
    () => new THREE.Quaternion().setFromEuler(new THREE.Euler(...rotation)),
    []
  );
  const rotationDrumCompositeQuaternion = useMemo(() => new THREE.Quaternion(), []);

  useFrame((state) => {
    time = state.clock.elapsedTime;

    const sine = Math.sin(time / 2);
    const rotationAngle = time * 0.5;
    const [baseX, baseY, baseZ] = movingPlatformBase;

    // Move platform
    sideMovePlatformRef.current?.setNextKinematicTranslation({
      x: baseX,
      y: baseY - sine,
      z: baseZ + 9 * sine,
    });

    // Elevate platforms
    for (let index = 0; index < elevatingPlatformRefs.current.length; index += 1) {
      const platform = elevatingPlatformRefs.current[index];
      const config = elevatingPlatforms[index];
      if (!platform || !config) {
        continue;
      }

      const [elevateX, elevateY, elevateZ] = config.basePosition;
      const amplitude = config.liftAmplitude ?? 2;
      const heightOffset = config.heightOffset ?? 2;

      platform.setNextKinematicTranslation({
        x: elevateX,
        y: elevateY + amplitude * sine + heightOffset,
        z: elevateZ,
      });
      platform.setNextKinematicRotation(
        quaternionRotation.setFromAxisAngle(yRotationAxies, rotationAngle)
      );
    }

    // Rotate platforms
    for (let index = 0; index < rotatingPlatformRefs.current.length; index += 1) {
      const platform = rotatingPlatformRefs.current[index];
      const config = rotatingPlatforms[index];
      if (!platform || !config) {
        continue;
      }

      const [baseX, baseY, baseZ] = config.basePosition;
      const amplitude = config.oscillationAmplitude ?? 2;
      const phase = config.oscillationPhase ?? 0;
      const platformSineOffset = Math.sin(time + phase) * amplitude;
      const platformRotationAngle = rotationAngle * (config.rotationSpeed ?? 1);

      platform.setNextKinematicTranslation({
        x: baseX,
        y: baseY,
        z: baseZ + platformSineOffset,
      });
      platform.setNextKinematicRotation(
        quaternionRotation.setFromAxisAngle(yRotationAxies, platformRotationAngle)
      );
    }

    // Rotate drum preserving base orientation
    rotationDrumRef.current?.setNextKinematicRotation(
      rotationDrumCompositeQuaternion
        .copy(rotationDrumBaseQuaternion)
        .multiply(quaternionRotation.setFromAxisAngle(xRotationAxies, rotationAngle))
    );
  });

  return (
    <>
      {/* Moving platform */}
      <RigidBody
        type="kinematicPosition"
        ref={sideMovePlatformRef}
        position={movingPlatformBase}
        colliders={false}
      >
        <CuboidCollider args={[2.5, 0.1, 2.5]} />
        <mesh receiveShadow castShadow>
          <boxGeometry args={[5, 0.2, 5]} />
          <meshStandardMaterial color={"moccasin"} />
        </mesh>
      </RigidBody>

      {/* Elevating platforms */}
      {elevatingPlatforms.map((platform, index) => (
        <RigidBody
          key={`elevating-${index}`}
          type="kinematicPosition"
          position={platform.basePosition}
          ref={(body) => {
            elevatingPlatformRefs.current[index] = body;
          }}
          colliders={false}
        >
          <Text
            scale={0.5}
            color="black"
            maxWidth={10}
            textAlign="center"
            position={[0, 2.5, 0]}
            rotation={platform.textRotation}
          >
            {platform.label}
          </Text>
          <CuboidCollider args={[2.5, 0.1, 2.5]} />
          <mesh receiveShadow castShadow>
            <boxGeometry args={[5, 0.2, 5]} />
            <meshStandardMaterial color={"moccasin"} />
          </mesh>
        </RigidBody>
      ))}

      {/* Rotating platforms */}
      {rotatingPlatforms.map((platform, index) => (
        <RigidBody
          key={`rotating-${index}`}
          type="kinematicPosition"
          position={platform.basePosition}
          ref={(body) => {
            rotatingPlatformRefs.current[index] = body ?? null;
          }}
          colliders={false}
        >
          <Text
            scale={0.5}
            color="black"
            maxWidth={10}
            textAlign="center"
            position={[0, 2.5, 0]}
          >
            {platform.label}
          </Text>
          {platform.shape === "box" ? (
            <>
              <CuboidCollider args={[2.5, 0.1, 2.5]} />
              <mesh receiveShadow castShadow>
                <boxGeometry args={[5, 0.2, 5]} />
                <meshStandardMaterial color={"#DB67D5"} />
              </mesh>
            </>
          ) : (
            <>
              <CylinderCollider args={[0.03, 2.5]} position={[0, 0.25, 0]} />
              <BallCollider args={[0.25]} />
              <mesh receiveShadow castShadow>
                <cylinderGeometry args={[2.5, 0.2, 0.5]} />
                <meshStandardMaterial color={"moccasin"} />
              </mesh>
            </>
          )}
        </RigidBody>
      ))}


      <RigidBody
        type="kinematicPosition"
        position={[-8, 12, -15]}
        colliders={false}
      >
        <CuboidCollider args={[2.5, 0.1, 4]} />
        <mesh receiveShadow castShadow>
          <boxGeometry args={[5, 0.2, 8]} />
          <meshStandardMaterial color={"#008DF6"} />
        </mesh>
      </RigidBody>
      
      <RigidBody
        type="kinematicPosition"
        position={[-6, 8, 3]}
        colliders={false}
        rotation={[0, Math.PI * 0.1, 0]}
      >
        <CuboidCollider args={[1, 0.1, 2]} />
        <mesh receiveShadow castShadow>
          <boxGeometry args={[2, 0.2, 4]} />
          <meshStandardMaterial color={"#F8C52C"} />
        </mesh>
      </RigidBody>
      {/* Rotating drum */}
      <RigidBody
        colliders={false}
        type="kinematicPosition"
        position={[-7.5, 9, -5]}
        rotation={rotation}
        ref={rotationDrumRef}
      >
        <group rotation={[0, 0, Math.PI / 2]}>
          <CylinderCollider args={[5, 1]} />
          <mesh receiveShadow>
            <cylinderGeometry args={[1, 1, 10, 32, 1, false, 0, Math.PI]} />
            <meshStandardMaterial color={"#F06968"} />
          </mesh>
          <mesh receiveShadow>
            <cylinderGeometry args={[1, 1, 10, 32, 1, false, Math.PI, Math.PI]} />
            <meshStandardMaterial color={"#4DE385"} />
          </mesh>
        </group>
      </RigidBody>
    </>
  );
}
